import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api, type ApiEnvelope } from './client';
import { useAuthStore } from '@/stores/useAuthStore';
import type {
  CandidateProfile,
  EmployerProfile,
  JobListing,
  JobMatch,
  SearchFilters,
  CandidateSearchResult,
  JobSearchResult,
  SearchResults,
  IdentityVerification,
  ProfileStrength,
  CandidateBadge,
  WorkExperience,
  ProfessionalAssociationMembership,
  ServiceOrder,
  BeneficiaryOrderView,
  ServiceCatalogueEntry,
  CreateServiceOrderRequest,
} from '@/types';

export interface LegalEntity {
  _id: string;
  _rev?: string;
  type: 'legalentity';
  urn: string;
  entityType: 'Individual' | 'Organisation';
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  companyName?: string;
  tradingName?: string;
  physicalAddress?: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  };
  bankAccount?: {
    bankName: string;
    accountNumber: string;
    accountType: 'cheque' | 'savings';
    branchCode: string;
  };
  nationalIds?: Array<{ type: string; number: string; country: string }>;
  status: string;
  metadata: { createdAt: string; createdBy: string; updatedAt: string; updatedBy: string };
}

export const queryKeys = {
  all: ['api'] as const,
  candidates: {
    all: ['api', 'candidates'] as const,
    list: (filters?: Partial<SearchFilters>) =>
      ['api', 'candidates', 'list', filters] as const,
    detail: (slug: string) => ['api', 'candidates', 'detail', slug] as const,
    profile: (id: string) => ['api', 'candidates', 'profile', id] as const,
  },
  employers: {
    all: ['api', 'employers'] as const,
    profile: (id: string) => ['api', 'employers', 'profile', id] as const,
  },
  jobs: {
    all: ['api', 'jobs'] as const,
    list: (filters?: Partial<SearchFilters>) =>
      ['api', 'jobs', 'list', filters] as const,
    detail: (id: string) => ['api', 'jobs', 'detail', id] as const,
  },
  matches: {
    all: ['api', 'matches'] as const,
    list: (candidateId: string) =>
      ['api', 'matches', 'list', candidateId] as const,
  },
  agents: {
    all: ['api', 'agents'] as const,
    list: (filters?: { tenantId?: string; status?: string }) =>
      ['api', 'agents', 'list', filters] as const,
    nearby: (h3Index: string) =>
      ['api', 'agents', 'nearby', h3Index] as const,
  },
};

export function useCandidates(filters?: Partial<SearchFilters>) {
  return useQuery({
    queryKey: queryKeys.candidates.list(filters),
    queryFn: () =>
      api.get<ApiEnvelope<CandidateProfile[]>>('/workseeking/candidates', {
        params: filters as Record<string, string | number | boolean | undefined>,
      }),
  });
}

export function useCandidate(slug: string) {
  return useQuery({
    queryKey: queryKeys.candidates.detail(slug),
    queryFn: () => api.get<ApiEnvelope<CandidateProfile>>(`/workseeking/candidates/${slug}`),
    enabled: !!slug,
  });
}

export function useCandidateProfile(id: string) {
  return useQuery({
    queryKey: queryKeys.candidates.profile(id),
    queryFn: () =>
      api.get<ApiEnvelope<CandidateProfile>>(`/workseeking/candidates/${id}`),
    enabled: !!id,
  });
}

/** Fetch LegalEntity by ID — used for editing and pre-populating forms. */
export function useLegalEntity(id: string) {
  return useQuery({
    queryKey: ['api', 'legal-entities', id],
    queryFn: () =>
      api.get<ApiEnvelope<LegalEntity>>(`/legal-entities/${id}`),
    enabled: !!id,
  });
}

export function useMyCandidateProfile() {
  const { isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: ['api', 'candidates', 'me'],
    queryFn: () =>
      api.get<ApiEnvelope<CandidateProfile>>('/workseeking/candidates/me'),
    enabled: isAuthenticated,
  });
}

/** Profile strength score for the current user (def:rosta:workseeking:profilestrength). */
export function useMyCandidateProfileStrength() {
  const { isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: ['api', 'candidates', 'me', 'strength'],
    queryFn: () =>
      api.get<ApiEnvelope<ProfileStrength>>('/workseeking/candidates/me/strength'),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
}

/** M09 S03 — badge shelf data for the signed-in candidate (owner view; server resolves the profile). */
export function useCandidateBadges() {
  return useQuery({
    queryKey: ['api', 'candidates', 'me', 'badges'],
    queryFn: () =>
      api.get<ApiEnvelope<CandidateBadge[]>>('/workseeking/candidates/me/badges'),
    staleTime: 30_000,
  });
}

/** Fetch the current user's employer profile (first one for the current tenant). */
export function useMyEmployerProfile() {
  const { data: sessionEnvelope } = useSessionInfo();
  const tenantId = sessionEnvelope?.data?.currentTenant?.id ?? '';
  return useQuery({
    queryKey: ['api', 'employers', 'my', tenantId],
    queryFn: () =>
      api.get<ApiEnvelope<EmployerProfile[]>>('/workseeking/employers', {
        params: { tenantId },
      }),
    enabled: !!tenantId,
    select: (res) => res.data?.[0] ?? null,
  });
}

export function useEmployerProfile(id: string) {
  return useQuery({
    queryKey: queryKeys.employers.profile(id),
    queryFn: () =>
      api.get<ApiEnvelope<EmployerProfile>>(`/workseeking/employers/${id}`),
    enabled: !!id,
  });
}

export function useUpdateEmployerProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EmployerProfile> }) =>
      api.patch<ApiEnvelope<EmployerProfile>>(`/workseeking/employers/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.employers.all });
    },
  });
}

export function useCreateEmployerProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, data }: { tenantId: string; data: Partial<EmployerProfile> }) =>
      api.post<ApiEnvelope<EmployerProfile>>(`/workseeking/employers?tenantId=${tenantId}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.employers.all });
    },
  });
}

export function useUpdateLegalEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LegalEntity> }) =>
      api.patch<ApiEnvelope<LegalEntity>>(`/legal-entities/${id}`, data),
    onSuccess: (_res, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['api', 'legal-entities', id] });
    },
  });
}

export function useJobs(filters?: Partial<SearchFilters>) {
  return useQuery({
    queryKey: queryKeys.jobs.list(filters),
    queryFn: () =>
      api.get<ApiEnvelope<JobListing[]>>('/workseeking/job-listings', {
        params: filters as Record<string, string | number | boolean | undefined>,
      }),
  });
}

export function useJob(id: string) {
  return useQuery({
    queryKey: queryKeys.jobs.detail(id),
    queryFn: () => api.get<ApiEnvelope<JobListing>>(`/workseeking/jobs/${id}`),
    enabled: !!id,
  });
}

/** Employer-scoped job listings — fetches only jobs belonging to a specific employer profile. */
export function useEmployerJobs(employerProfileId: string, tenantId: string) {
  return useQuery({
    queryKey: ['api', 'jobs', 'employer', employerProfileId],
    queryFn: () =>
      api.get<ApiEnvelope<JobListing[]>>(`/workseeking/employers/${employerProfileId}/jobs`, {
        params: { tenantId },
      }),
    enabled: !!employerProfileId && !!tenantId,
  });
}

/** Close a job listing (status → Closed). */
export function useCloseJobListing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      api.post<JobListing>(`/workseeking/jobs/${jobId}/close`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
  });
}

/** Most recently added, publicly-viewable job listings for the landing page.
 *  MVP: simple recency feed (no ranking heuristic yet). */
export function useRecentJobs(limit = 6) {
  return useQuery({
    queryKey: ['api', 'jobs', 'recent', limit],
    queryFn: () =>
      api.get<ApiEnvelope<JobListing[]>>('/workseeking/job-listings', {
        params: { status: 'Open', limit },
      }),
    staleTime: 5 * 60 * 1000, // 5 min — landing feed is low-velocity, avoids refetch on every visit
  });
}

/** Most recently registered, marketplace-visible candidate profiles for the
 *  landing page. MVP: simple recency feed (no ranking heuristic yet). */
export function useRecentCandidates(limit = 10) {
  return useQuery({
    queryKey: ['api', 'candidates', 'recent', limit],
    queryFn: () =>
      api.get<ApiEnvelope<CandidateProfile[]>>('/workseeking/candidates/profiles', {
        params: { limit },
      }),
    staleTime: 5 * 60 * 1000, // 5 min — matches useRecentJobs
  });
}

/** Marketplace stats for landing page (open contracts, tradespeople, employers, categories). */
export interface MarketplaceStats {
  openContracts: number;
  skilledTradespeople: number;
  homeownersAndBusinesses: number;
  trainingProviders: number;
  categories: Array<{ ofoCode: string; label: string; count: number }>;
}

export function useMarketplaceStats() {
  return useQuery({
    queryKey: ['api', 'marketplace-stats'],
    queryFn: () => api.get<ApiEnvelope<MarketplaceStats>>('/workseeking/search/stats'),
    staleTime: 5 * 60 * 1000, // 5 min — matches server-side cache TTL
  });
}

export interface MarketplaceFilters {
  query?: string;
  ofoCode?: string;
  skills?: string[];
  categories?: string[];
  availability?: string;
  workType?: string;
  province?: string;
  city?: string;
  h3Origin?: string;
  radiusKm?: number;
  coida?: boolean;
  uif?: boolean;
  salaryMin?: number;
  salaryMax?: number;
  page?: number;
  pageSize?: number;
}

export function useSearchCandidates(filters?: MarketplaceFilters) {
  return useQuery({
    queryKey: ['api', 'search', 'candidates', filters],
    queryFn: () =>
      api.get<SearchResults<CandidateSearchResult>>('/workseeking/search/candidates', {
        params: {
          q: filters?.query,
          ofoCode: filters?.ofoCode,
          availability: filters?.availability,
          workType: filters?.workType,
          province: filters?.province,
          city: filters?.city,
          h3Origin: filters?.h3Origin,
          radiusKm: filters?.radiusKm,
          salaryMin: filters?.salaryMin,
          salaryMax: filters?.salaryMax,
          page: filters?.page ?? 1,
          limit: filters?.pageSize ?? 20,
        },
      }),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSearchJobs(filters?: MarketplaceFilters) {
  return useQuery({
    queryKey: ['api', 'search', 'jobs', filters],
    queryFn: () =>
      api.get<SearchResults<JobSearchResult>>('/workseeking/search/jobs', {
        params: {
          q: filters?.query,
          ofoCode: filters?.ofoCode,
          workType: filters?.workType,
          province: filters?.province,
          city: filters?.city,
          page: filters?.page ?? 1,
          limit: filters?.pageSize ?? 20,
        },
      }),
    staleTime: 5 * 60 * 1000, // 5 min — same query serves from cache
  });
}

export function useCandidateBySlug(slug: string, h3Origin?: string) {
  return useQuery({
    queryKey: ['api', 'search', 'candidates', 'slug', slug, h3Origin],
    queryFn: () =>
      api.get<ApiEnvelope<CandidateSearchResult>>(`/workseeking/search/candidates/${slug}`, {
        params: h3Origin ? { h3Origin } : undefined,
      }),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useJobById(id: string) {
  return useQuery({
    queryKey: ['api', 'search', 'jobs', 'id', id],
    queryFn: () => api.get<ApiEnvelope<JobSearchResult>>(`/workseeking/jobs/${id}`),
    enabled: !!id,
  });
}

export function useMatches(candidateId: string) {
  return useQuery({
    queryKey: queryKeys.matches.list(candidateId),
    queryFn: () =>
      api.get<ApiEnvelope<JobMatch[]>>('/workseeking/job-matches', {
        params: { candidateProfile: candidateId },
      }),
    enabled: !!candidateId,
  });
}

export function useCreateCandidateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, data }: { tenantId: string; data: Partial<CandidateProfile> }) =>
      api.post<CandidateProfile>('/workseeking/candidates', data, { params: { tenantId } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.candidates.all });
    },
  });
}

export function useUpdateCandidateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CandidateProfile> }) => {
      const tenantId = id.includes(':') ? id.split(':')[0] : '';
      return api.patch<CandidateProfile>(`/workseeking/candidates/${id}`, data, { params: { tenantId } });
    },
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.candidates.profile(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.candidates.all });
    },
  });
}

export function useCreateJobListing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<JobListing>) =>
      api.post<JobListing>('/workseeking/jobs', data, { params: { tenantId: data.tenant?.id } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
  });
}

export function useUpdateJobListing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<JobListing> }) =>
      api.patch<JobListing>(`/workseeking/jobs/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
  });
}

export function useApplyToJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { jobListingId: string; candidateProfileId: string }) =>
      api.post<JobMatch>('/workseeking/job-matches', {
        jobListing: { type: 'joblisting', id: data.jobListingId },
        candidateProfile: { type: 'candidateprofile', id: data.candidateProfileId },
        status: 'Applied',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.matches.all });
    },
  });
}

export function useAcceptCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) =>
      api.patch<JobMatch>(`/workseeking/job-matches/${matchId}`, {
        status: 'Shortlisted',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.matches.all });
    },
  });
}

export const identityVerificationKeys = {
  all: ['api', 'identity-verification'] as const,
  latest: (candidateId: string) =>
    ['api', 'identity-verification', 'latest', candidateId] as const,
};

export function useLatestVerification(candidateId: string) {
  return useQuery({
    queryKey: identityVerificationKeys.latest(candidateId),
    queryFn: () =>
      api.get<ApiEnvelope<IdentityVerification | null>>(
        `/identity-verification/candidate/${candidateId}`,
      ),
    enabled: !!candidateId,
  });
}

export function useRequestVerification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      candidateId: string;
      idNumber: string;
      fullName: string;
      idType?: 'SA_ID' | 'PASSPORT';
    }) =>
      api.post<IdentityVerification>('/identity-verification/verify', data, {
        params: { candidateId: data.candidateId },
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: identityVerificationKeys.latest(variables.candidateId),
      });
    },
  });
}

const EXPERIENCE_LIMIT = 5;

export function useCandidateExperience(candidateId: string) {
  const tenantId = candidateId.includes(':') ? candidateId.split(':')[0] : '';
  return useQuery({
    queryKey: ['api', 'candidates', candidateId, 'experience'],
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<WorkExperience[]>>(
        `/workseeking/candidates/${candidateId}/experience`,
        { params: { tenantId } },
      );
      const all = (res.data ?? [])
        .filter((e: WorkExperience) => e.status === 'Active')
        .sort((a: WorkExperience, b: WorkExperience) => (b.metadata.createdAt ?? '').localeCompare(a.metadata.createdAt ?? ''));
      return all.slice(0, EXPERIENCE_LIMIT);
    },
    enabled: !!candidateId && !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateWorkExperience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, data }: { candidateId: string; data: Partial<WorkExperience> }) => {
      const tenantId = candidateId.includes(':') ? candidateId.split(':')[0] : '';
      return api.post<ApiEnvelope<WorkExperience>>(
        `/workseeking/candidates/${candidateId}/experience`,
        data,
        { params: { tenantId } },
      );
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['api', 'candidates', vars.candidateId, 'experience'] });
    },
  });
}

export function useUpdateWorkExperience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, candidateId }: { id: string; data: Partial<WorkExperience>; candidateId: string }) => {
      const tenantId = id.includes(':') ? id.split(':')[0] : '';
      return api.patch<ApiEnvelope<WorkExperience>>(`/workseeking/experience/${id}`, data, { params: { tenantId } }).then((res) => ({ ...res, candidateId }));
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['api', 'candidates', vars.candidateId, 'experience'] });
    },
  });
}

export function useDeleteWorkExperience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, candidateId }: { id: string; candidateId: string }) => {
      const tenantId = id.includes(':') ? id.split(':')[0] : '';
      return api.delete(`/workseeking/experience/${id}`, { params: { tenantId } }).then(() => ({ candidateId }));
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['api', 'candidates', vars.candidateId, 'experience'] });
    },
  });
}

export function useCreateJobPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, data }: { candidateId: string; data: Record<string, unknown> }) => {
      const tenantId = candidateId.includes(':') ? candidateId.split(':')[0] : '';
      return api.post<ApiEnvelope<Record<string, unknown>>>(
        `/workseeking/candidates/${candidateId}/preferences`,
        data,
        { params: { tenantId } },
      ).then((res) => ({ ...res, candidateId }));
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['api', 'candidates', vars.candidateId] });
    },
  });
}

export function useUpdateJobPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, candidateId }: { id: string; data: Record<string, unknown>; candidateId: string }) => {
      const tenantId = id.includes(':') ? id.split(':')[0] : '';
      return api.patch<ApiEnvelope<Record<string, unknown>>>(`/workseeking/preferences/${id}`, data, { params: { tenantId } }).then((res) => ({ ...res, candidateId }));
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['api', 'candidates', vars.candidateId] });
    },
  });
}

export function useDeleteJobPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, candidateId }: { id: string; candidateId: string }) => {
      const tenantId = id.includes(':') ? id.split(':')[0] : '';
      return api.delete(`/workseeking/preferences/${id}`, { params: { tenantId } }).then(() => ({ candidateId }));
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['api', 'candidates', vars.candidateId] });
    },
  });
}

// ── Professional Association Memberships ─────────────────────────────────────

export const professionalAssociationKeys = {
  all: ['api', 'professional-associations'] as const,
  candidate: (candidateId: string) =>
    ['api', 'professional-associations', 'candidate', candidateId] as const,
};

export function useCandidateAssociations(legalEntityId: string) {
  return useQuery({
    queryKey: professionalAssociationKeys.candidate(legalEntityId),
    queryFn: () =>
      api.get<ApiEnvelope<ProfessionalAssociationMembership[]>>(
        `/professional-association-memberships/candidate/${legalEntityId}`,
      ),
    enabled: !!legalEntityId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      legalEntityId: string;
      association: { name: string; countryCode?: string; registrationNumber?: string; website?: string };
      membershipNumber: string;
      membershipLevel?: string;
      issuedDate?: string;
      expiryDate?: string;
    }) => {
      const tenantId = data.legalEntityId.includes(':') ? data.legalEntityId.split(':')[0] : '';
      const { legalEntityId, ...rest } = data;
      return api.post<ProfessionalAssociationMembership>(
        '/professional-association-memberships',
        {
          ...rest,
          legalEntity: { type: 'legalentity' as const, id: legalEntityId },
          tenant: { type: 'tenant' as const, id: tenantId },
          status: 'Pending',
          verified: false,
        },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: professionalAssociationKeys.all });
    },
  });
}

export function useUpdateMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ProfessionalAssociationMembership> }) => {
      const tenantId = id.includes(':') ? id.split(':')[0] : '';
      return api.patch<ProfessionalAssociationMembership>(
        `/professional-association-memberships/${id}`,
        data,
        { params: { tenantId } },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: professionalAssociationKeys.all });
    },
  });
}

export function useDeleteMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => {
      const tenantId = id.includes(':') ? id.split(':')[0] : '';
      return api.delete(`/professional-association-memberships/${id}`, {
        params: { tenantId },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: professionalAssociationKeys.all });
    },
  });
}

// ── NPS ──────────────────────────────────────────────────────────────────────

import type { NpsAggregateResult, NpsMonthlyResult } from '@/types';

export function useCandidateNpsAggregate(candidateId: string) {
  return useQuery({
    queryKey: ['api', 'nps', 'aggregate', 'subject', 'candidateprofile', candidateId],
    queryFn: () =>
      api.get<ApiEnvelope<NpsAggregateResult>>(
        `/nps/aggregate/subject/candidateprofile/${candidateId}`,
      ),
    enabled: !!candidateId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCandidateNpsMonthly(surveyId: string) {
  return useQuery({
    queryKey: ['api', 'nps', 'aggregate', 'survey', surveyId, 'monthly'],
    queryFn: () =>
      api.get<ApiEnvelope<NpsMonthlyResult[]>>(
        `/nps/aggregate/survey/${surveyId}/monthly`,
      ),
    enabled: !!surveyId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useActiveSurvey(subjectType: string) {
  return useQuery({
    queryKey: ['api', 'nps', 'surveys', 'active', subjectType],
    queryFn: () =>
      api.get<ApiEnvelope<{ _id: string; status: string; subjectType: string }>>(
        `/nps/surveys/active/${subjectType}`,
      ),
    enabled: !!subjectType,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateNpsResponse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      survey: { type: string; id: string };
      subject: { type: string; id: string };
      score: number;
      comment?: string;
    }) => api.post<ApiEnvelope<{ id: string; rev: string }>>('/nps/responses', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api', 'nps'] });
    },
  });
}

// ── Session Info ──────────────────────────────────────────────────────────────

export interface SessionInfo {
  sessionId: string;
  userId: string;
  keycloakId?: string;
  currentTenant: { id: string; name?: string };
  availableTenants: Array<{ id: string; name?: string }>;
  roles: string[];
  issuedAt: string;
  expiresAt: string;
  lastActiveAt: string;
  isActive: boolean;
}

export function useSessionInfo() {
  const { isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: ['api', 'auth', 'session'],
    queryFn: () => api.get<ApiEnvelope<SessionInfo>>('/auth/session'),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

// ── Skills CRUD ──────────────────────────────────────────────────────────────

export interface SkillData {
  _id?: string;
  name: string;
  category?: string;
  proficiency: 'Beginner' | 'Intermediate' | 'Expert';
  yearsExperience?: number;
  endorsements?: number;
  skillCatalog?: { type: 'skillcatalog'; id: string };
  ofoCode?: { type: 'ofocode'; id: string };
  status?: 'Active' | 'Inactive';
}

export function useCandidateSkills(candidateId: string) {
  const tenantId = candidateId.includes(':') ? candidateId.split(':')[0] : '';
  return useQuery({
    queryKey: ['api', 'candidates', candidateId, 'skills'],
    queryFn: () =>
      api.get<ApiEnvelope<SkillData[]>>(
        `/workseeking/candidates/${candidateId}/skills`,
        { params: { tenantId } },
      ),
    enabled: !!candidateId && !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, data }: { candidateId: string; data: Omit<SkillData, '_id'> }) => {
      const tenantId = candidateId.includes(':') ? candidateId.split(':')[0] : '';
      return api.post<SkillData>(
        `/workseeking/candidates/${candidateId}/skills`,
        { ...data, status: data.status ?? 'Active' },
        { params: { tenantId } },
      );
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['api', 'candidates', vars.candidateId, 'skills'] });
    },
  });
}

export function useUpdateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SkillData>; candidateId: string }) => {
      const tenantId = id.includes(':') ? id.split(':')[0] : '';
      return api.patch<SkillData>(`/workseeking/skills/${id}`, data, { params: { tenantId } });
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['api', 'candidates', vars.candidateId, 'skills'] });
    },
  });
}

export function useDeleteSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rev }: { id: string; rev?: string; candidateId: string }) => {
      const tenantId = id.includes(':') ? id.split(':')[0] : '';
      return api.delete(`/workseeking/skills/${id}`, {
        params: { tenantId, rev },
      });
    },
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['api', 'candidates', vars.candidateId, 'skills'] });
    },
  });
}

export function useAgents(filters?: { tenantId?: string; status?: string }) {
  return useQuery({
    queryKey: queryKeys.agents.list(filters),
    queryFn: () =>
      api.get<ApiEnvelope<any[]>>('/workseeking/agents', {
        params: filters as Record<string, string | undefined>,
      }),
    staleTime: 2 * 60 * 1000,
  });
}

export function useNearbyAgents(h3Index: string) {
  return useQuery({
    queryKey: queryKeys.agents.nearby(h3Index),
    queryFn: () =>
      api.get<ApiEnvelope<any[]>>('/workseeking/agents/nearby', {
        params: { h3Index },
      }),
    enabled: !!h3Index,
    staleTime: 2 * 60 * 1000,
  });
}

// ── Training Providers ──

export interface ProviderSearchResult {
  _id: string;
  providerName: string;
  accreditationStatus: string;
  accreditationExpiry?: string;
  industrySectors: string[];
  physicalAddress: { city: string; province: string; h3Index?: string };
  ofoCodes: string[];
  performanceMetrics?: { coursesDelivered: number; totalLearners: number };
  tier: 'Organic' | 'Preferred';
  contactPhone: string;
  contactEmail?: string;
  distanceKm?: number;
  courses: Array<{
    _id: string;
    courseName: string;
    price?: number;
    duration?: string;
    nqfLevel?: number;
    learnerCount: number;
    ofoCodes: string[];
  }>;
}

export function useSearchProviders(filters?: MarketplaceFilters) {
  return useQuery({
    queryKey: ['api', 'search', 'providers', filters],
    queryFn: () =>
      api.get<SearchResults<ProviderSearchResult>>('/workseeking/search/providers', {
        params: {
          q: filters?.query,
          ofoCode: filters?.ofoCode,
          province: filters?.province,
          h3Origin: filters?.h3Origin,
          page: filters?.page ?? 1,
          limit: filters?.pageSize ?? 20,
        },
      }),
    staleTime: 5 * 60 * 1000,
  });
}

export function useProviderProfile(id: string, h3Origin?: string) {
  return useQuery({
    queryKey: ['api', 'search', 'providers', 'detail', id, h3Origin],
    queryFn: () =>
      api.get<ApiEnvelope<ProviderSearchResult>>(`/workseeking/search/providers/${id}`, {
        params: h3Origin ? { h3Origin } : undefined,
      }),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Map data hooks (Sprint 02+) ──────────────────────────────────────────────

/** GeoJSON FeatureCollection with H3 polygon geometries. */
export interface MapFeatureCollection {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: { type: 'Polygon'; coordinates: number[][][] };
    properties: Record<string, unknown>;
  }>;
}

/** GeoJSON Feature with a single polygon geometry. */
export interface MapFeature {
  type: 'Feature';
  geometry: { type: 'Polygon'; coordinates: number[][][] };
  properties: Record<string, unknown>;
}

/**
 * Fetch candidate H3 cells for the map layer around an origin.
 *
 * @see GET /api/v1/geocoding/map/candidates
 */
export function useMapCandidates(
  h3Index: string | undefined,
  k: number = 2,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['api', 'map', 'candidates', h3Index, k],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: MapFeatureCollection }>(
        '/geocoding/map/candidates',
        { params: { h3Index: h3Index!, k } },
      );
      return res.data;
    },
    enabled: enabled && !!h3Index,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Fetch density data for the map layer around an origin.
 *
 * @see GET /api/v1/geocoding/map/density
 */
export function useMapDensity(
  h3Index: string | undefined,
  k: number = 2,
  metric: 'candidates' | 'jobs' | 'providers' = 'candidates',
  enabled: boolean = true,
  resolution?: number,
) {
  return useQuery({
    queryKey: ['api', 'map', 'density', h3Index, k, metric, resolution ?? null],
    queryFn: async () => {
      const params: Record<string, string | number> = { h3Index: h3Index!, k, metric };
      if (resolution !== undefined) params.resolution = resolution;
      const res = await api.get<{ success: boolean; data: MapFeatureCollection }>(
        '/geocoding/map/density',
        { params },
      );
      return res.data;
    },
    enabled: enabled && !!h3Index,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Map a MapLibre zoom level to an appropriate H3 resolution and ring size (k)
 * for density aggregation. Coarser zooms use larger hexes covering wider areas;
 * finer zooms use smaller hexes for suburb-level detail.
 *
 * Resolution ↔ scale reference:
 *   res 3: ~110 km hex (country)   | res 4: ~40 km  (province)
 *   res 5: ~15 km hex (regional)   | res 6: ~5.6 km (city)
 *   res 7: ~2.2 km hex (district)  | res 8: ~840 m  (suburb)
 */
export function zoomToH3ResolutionAndK(zoom: number): { resolution: number; k: number } {
  if (zoom < 5) return { resolution: 3, k: 7 };       // Country scale — res-3 k=7 covers ~1500km (all of SA)
  if (zoom < 7) return { resolution: 4, k: 8 };       // Province scale — res-4 k=8 covers ~640km
  if (zoom < 9) return { resolution: 5, k: 10 };      // Regional scale — res-5 k=10 covers ~300km
  if (zoom < 11) return { resolution: 6, k: 10 };     // City scale — res-6 k=10 covers ~112km
  if (zoom < 13) return { resolution: 7, k: 10 };     // District scale — res-7 k=10 covers ~44km
  return { resolution: 8, k: 10 };                     // Suburb scale — res-8 k=10 covers ~17km
}

/**
 * Fetch a single H3 cell polygon boundary.
 *
 * @see GET /api/v1/geocoding/h3/polygon
 */
export function useH3Polygon(h3Index: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: ['api', 'h3', 'polygon', h3Index],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: MapFeature }>(
        '/geocoding/h3/polygon',
        { params: { h3Index: h3Index! } },
      );
      return res.data;
    },
    enabled: enabled && !!h3Index,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch an H3 ring expansion around a cell.
 *
 * @see GET /api/v1/geocoding/h3/ring
 */
export function useUploadFile() {
  return useMutation({
    mutationFn: async ({ file, subsystem, category, ownerId, ownerType }: {
      file: File;
      subsystem?: string;
      category?: string;
      ownerId?: string;
      ownerType?: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      const params: Record<string, string> = {};
      if (subsystem) params.subsystem = subsystem;
      if (category) params.category = category;
      if (ownerId) params.ownerId = ownerId;
      if (ownerType) params.ownerType = ownerType;
      return api.upload<{ success: boolean; data: { id: string; rev: string; fileRef: { type: 'filereference'; id: string } } }>(
        '/files',
        formData,
        { params },
      );
    },
  });
}

export function useH3Ring(
  h3Index: string | undefined,
  k: number = 1,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ['api', 'h3', 'ring', h3Index, k],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: MapFeatureCollection }>(
        '/geocoding/h3/ring',
        { params: { h3Index: h3Index!, k } },
      );
      return res.data;
    },
    enabled: enabled && !!h3Index,
    staleTime: 10 * 60 * 1000,
  });
}

// ── Sponsored Services (Milestone 07, Sprint 06) ─────────────────────────────

export const serviceOrderKeys = {
  all: ['api', 'service-orders'] as const,
  detail: (id: string) => ['api', 'service-orders', 'detail', id] as const,
  mine: () => ['api', 'service-orders', 'mine'] as const,
  beneficiary: () => ['api', 'service-orders', 'beneficiary'] as const,
};

export const serviceCatalogueKeys = {
  all: ['api', 'service-catalogue'] as const,
  entry: (serviceKey: string) =>
    ['api', 'service-catalogue', serviceKey] as const,
};

/** Orders the current user sponsored (payer = session user). */
export function useMySponsoredOrders() {
  const { isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: serviceOrderKeys.mine(),
    queryFn: () =>
      api.get<ApiEnvelope<ServiceOrder[]>>('/service-orders/mine'),
    enabled: isAuthenticated,
  });
}

/**
 * Open/paid orders where the current user is the beneficiary (redacted
 * `BeneficiaryOrderView` — sponsor display name only for Named sponsors).
 */
export function useMyBeneficiaryOrders() {
  const { isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: serviceOrderKeys.beneficiary(),
    queryFn: () =>
      api.get<ApiEnvelope<BeneficiaryOrderView[]>>(
        '/service-orders/beneficiary/me',
      ),
    enabled: isAuthenticated,
  });
}

/**
 * Trust-model polling interval (S04 T4.4): while the server status is
 * `PendingPayment` or `Paid`, refetch every 3s — the client payment callback
 * alone never renders as authoritative. Terminal statuses stop polling.
 */
export function serviceOrderPollInterval(
  status: ServiceOrder['status'] | undefined,
): number | false {
  return status === 'PendingPayment' || status === 'Paid' ? 3000 : false;
}

/**
 * Single order with trust-model polling (S04 T4.4): while the server status
 * is `PendingPayment` or `Paid`, refetch every 3s — the client payment
 * callback alone never renders as authoritative.
 */
export function useServiceOrder(id: string | undefined) {
  return useQuery({
    queryKey: serviceOrderKeys.detail(id ?? ''),
    queryFn: () =>
      api.get<ApiEnvelope<ServiceOrder>>(`/service-orders/${id}`),
    enabled: !!id,
    refetchInterval: (query) =>
      serviceOrderPollInterval(query.state.data?.data?.status),
  });
}

/** Create a sponsored order — price comes from the server (D3, never the client). */
export function useCreateServiceOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateServiceOrderRequest) =>
      api.post<ApiEnvelope<ServiceOrder>>('/service-orders', data),
    onSuccess: (envelope) => {
      const order = envelope?.data;
      if (order?._id) {
        void queryClient.invalidateQueries({
          queryKey: serviceOrderKeys.detail(order._id),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: serviceOrderKeys.mine(),
      });
    },
  });
}

/** Display price for a sponsored service — fail closed (404 when missing/inactive). */
export function useServiceCatalogueEntry(serviceKey: string | undefined) {
  return useQuery({
    queryKey: serviceCatalogueKeys.entry(serviceKey ?? ''),
    queryFn: () =>
      api.get<ApiEnvelope<ServiceCatalogueEntry>>(
        `/service-catalogue/${serviceKey}`,
      ),
    enabled: !!serviceKey,
    staleTime: 5 * 60 * 1000,
  });
}

/** Cancel a `PendingPayment` order (sponsor action). */
export function useCancelServiceOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<ApiEnvelope<ServiceOrder>>(
        `/service-orders/${id}/cancel`,
        {},
      ),
    onSuccess: (_envelope, id) => {
      void queryClient.invalidateQueries({
        queryKey: serviceOrderKeys.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: serviceOrderKeys.mine(),
      });
    },
  });
}
